import React, { useState } from "react";
import {
  Button,
  Modal,
  Input,
  Select,
  DatePicker,
  Form,
  Row,
  Col,
} from "antd";
import {
  PlusOutlined,
  QrcodeOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { hasAnyPermission, useAuth } from "../../../services/auth";
import { getApiErrorDetails } from "../../../services/api-utils";
import { createWarehouseDevices } from "../../../services/warehouse-devices";
import { QrReader } from "react-qr-reader";
import dayjs from "dayjs";

const { Option } = Select;

const getCurrentDate = () => {
  return dayjs().format("YYYY-MM-DD");
};

const DEVICE_TYPE_LABELS = ["S3", "S7", "S4", "LN", "BLE", "GW"];

const formatBackendErrorContent = (error) => {
  const details = getApiErrorDetails(error);
  const failedDevice = error?.devicePayload;
  const failedIndex = Number.isInteger(error?.deviceIndex)
    ? error.deviceIndex + 1
    : null;

  return (
    <div>
      {failedIndex && (
        <p>
          <strong>Thiết bị lỗi:</strong> #{failedIndex}
          {failedDevice?.qr_string ? ` - ${failedDevice.qr_string}` : ""}
          {failedDevice?.device_type !== undefined
            ? ` (${DEVICE_TYPE_LABELS[Number(failedDevice.device_type)] || failedDevice.device_type})`
            : ""}
        </p>
      )}
      {(details.status || details.statusText) && (
        <p>
          <strong>HTTP:</strong> {[details.status, details.statusText].filter(Boolean).join(" ")}
        </p>
      )}
      <p>
        <strong>Lỗi backend:</strong> {details.message}
      </p>
      {details.messages.length > 1 && (
        <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
          {details.messages.slice(1, 6).map((message, index) => (
            <li key={index}>{message}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

const AddDeviceForm = ({ open, onClose, onAddComplete }) => {
  const [user] = useAuth();
  const [scannerOpen, setScannerOpen] = useState(false);
  const [currentQrIndex, setCurrentQrIndex] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const today = getCurrentDate();

  if (!hasAnyPermission(user, ["warehouse_edit", "warehouse_manage"])) {
    return null;
  }

  const handleQrScan = (result, error) => {
    if (result && currentQrIndex !== null) {
      const qrString = result.text;
      const fields = document.getElementsByName(
        `devices[${currentQrIndex}].qr_string`
      );
      if (fields.length > 0) {
        fields[0].value = qrString;
      }
      setScannerOpen(false);
      setCurrentQrIndex(null);
    }
    if (error) console.error(error);
  };

  const handleSubmit = (values) => {
    // Validate và ép kiểu coefficients cho từng thiết bị
    const devices = values.devices.map((device, idx) => {
      const coeffs = { ...device.coefficients };
      let hasInvalid = false;
      Object.keys(coeffs).forEach((key) => {
        const val = coeffs[key];
        if (val === "" || isNaN(parseFloat(val))) {
          hasInvalid = true;
        } else {
          coeffs[key] = parseFloat(val);
        }
      });
      if (hasInvalid) {
        Modal.error({
          title: "Lỗi nhập hệ số",
          content: `Thiết bị thứ ${idx + 1}: Tất cả các hệ số phải là số thực hợp lệ!`,
        });
        throw new Error("Invalid coefficients");
      }
      return { ...device, coefficients: coeffs };
    });

    setIsSubmitting(true);
    createWarehouseDevices(devices)
      .then((res) => {
        if (!!res) {
          onClose();
          onAddComplete();
        }
      })
      .catch((err) => {
        console.error("Error: ", err);
        Modal.error({
          title: "Không thể thêm thiết bị",
          content: formatBackendErrorContent(err),
          width: 640,
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <Modal
      title="Thêm thiết bị"
      open={open}
      onCancel={isSubmitting ? undefined : onClose}
      footer={null}
      width="100%"
      style={{ maxWidth: "85vw", margin: "0 auto" }}
      maskClosable={!isSubmitting}
    >
      <Form
        name="addDevicesForm"
        onFinish={handleSubmit}
        layout="vertical"
        initialValues={{
          devices: [
            {
              qr_string: "",
              device_type: "",
              status: 2,
              lot: "3",
              user: user?.email || "",
              imported_at: dayjs(today),
              tested_at: dayjs(today),
              group: "3",
              note: "",
            },
          ],
        }}
      >
        <Form.List name="devices">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }, index) => (
                <div
                  key={key}
                  style={{
                    border: "1px solid #d9d9d9",
                    borderRadius: "8px",
                    padding: "16px",
                    marginBottom: "16px",
                    backgroundColor: "#fafafa",
                    position: "relative",
                  }}
                >
                  <Button
                    shape="circle"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => remove(name)}
                    style={{
                      position: "absolute",
                      top: "8px",
                      right: "8px",
                      zIndex: 1,
                    }}
                  />
                  <Row gutter={[8, 8]}>
                    <Col xs={24} sm={12} lg={8}>
                      <Form.Item
                        {...restField}
                        name={[name, "qr_string"]}
                        label="Mã QR"
                        rules={[{ required: true }]}
                      >
                        <Input
                          placeholder="QR Code"
                          size="large"
                          suffix={
                            <Button
                              size="small"
                              type="link"
                              icon={<QrcodeOutlined />}
                              onClick={() => {
                                setCurrentQrIndex(index);
                                setScannerOpen(true);
                              }}
                            />
                          }
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={12} sm={12} lg={8}>
                      <Form.Item
                        {...restField}
                        name={[name, "device_type"]}
                        label="Loại thiết bị"
                        rules={[{ required: true }]}
                      >
                        <Select size="large" placeholder="Chọn loại thiết bị">
                          {["S3", "S7", "S4", "LN", "BLE", "GW"].map(
                            (type, idx) => (
                              <Option key={idx} value={idx}>
                                {type}
                              </Option>
                            )
                          )}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={12} sm={12} lg={8}>
                      <Form.Item
                        {...restField}
                        name={[name, "status"]}
                        label="Trạng thái"
                        rules={[{ required: true }]}
                      >
                        <Select size="large" placeholder="Chọn trạng thái">
                          {[
                            { value: 0, label: "Khởi tạo" },
                            { value: 1, label: "Kiểm tra" },
                            { value: 2, label: "Tồn kho" },
                            { value: 3, label: "Kích hoạt" },
                            { value: 4, label: "Hoàn trả" },
                          ].map((status) => (
                            <Option
                              key={status.value}
                              value={status.value}
                              disabled={status.value === 3}
                            >
                              {status.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={12} sm={12} lg={8}>
                      <Form.Item
                        {...restField}
                        name={[name, "imported_at"]}
                        label="Ngày nhập"
                      >
                        <DatePicker
                          style={{ width: "100%" }}
                          size="large"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={12} sm={12} lg={8}>
                      <Form.Item
                        {...restField}
                        name={[name, "tested_at"]}
                        label="Ngày kiểm tra"
                      >
                        <DatePicker
                          style={{ width: "100%" }}
                          size="large"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={12} sm={12} lg={8}>
                      <Form.Item
                        {...restField}
                        name={[name, "lot"]}
                        label="Lô nhập"
                      >
                        <Input size="large" placeholder="Lot" />
                      </Form.Item>
                    </Col>
                    <Col xs={12} sm={12} lg={8}>
                      <Form.Item
                        {...restField}
                        name={[name, "group"]}
                        label="Nhóm"
                      >
                        <Input size="large" placeholder="Group" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} lg={16}>
                      <Form.Item
                        {...restField}
                        name={[name, "note"]}
                        label="Ghi chú"
                      >
                        <Input.TextArea
                          size="large"
                          placeholder="Note"
                          rows={1}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} lg={24}>
                      <Row gutter={[8, 8]}>
                        <Col xs={12} sm={8} lg={4}>
                          <Form.Item
                            {...restField}
                            name={[name, "coefficients", "a_ec"]}
                            label="a_ec"
                            rules={[
                              { required: true, message: "Nhập a_ec!" },
                              {
                                validator: (_, value) =>
                                  isNaN(parseFloat(value))
                                    ? Promise.reject("Phải là số thực!")
                                    : Promise.resolve(),
                              },
                            ]}
                            initialValue={0}
                          >
                            <Input placeholder="a_ec" />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={8} lg={4}>
                          <Form.Item
                            {...restField}
                            name={[name, "coefficients", "b_ec"]}
                            label="b_ec"
                            rules={[
                              { required: true, message: "Nhập b_ec!" },
                              {
                                validator: (_, value) =>
                                  isNaN(parseFloat(value))
                                    ? Promise.reject("Phải là số thực!")
                                    : Promise.resolve(),
                              },
                            ]}
                            initialValue={1}
                          >
                            <Input placeholder="b_ec" />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={8} lg={4}>
                          <Form.Item
                            {...restField}
                            name={[name, "coefficients", "c_ec"]}
                            label="c_ec"
                            rules={[
                              { required: true, message: "Nhập c_ec!" },
                              {
                                validator: (_, value) =>
                                  isNaN(parseFloat(value))
                                    ? Promise.reject("Phải là số thực!")
                                    : Promise.resolve(),
                              },
                            ]}
                            initialValue={0}
                          >
                            <Input placeholder="c_ec" />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={8} lg={4}>
                          <Form.Item
                            {...restField}
                            name={[name, "coefficients", "a_ph"]}
                            label="a_ph"
                            rules={[
                              { required: true, message: "Nhập a_ph!" },
                              {
                                validator: (_, value) =>
                                  isNaN(parseFloat(value))
                                    ? Promise.reject("Phải là số thực!")
                                    : Promise.resolve(),
                              },
                            ]}
                            initialValue={0}
                          >
                            <Input placeholder="a_ph" />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={8} lg={4}>
                          <Form.Item
                            {...restField}
                            name={[name, "coefficients", "b_ph"]}
                            label="b_ph"
                            rules={[
                              { required: true, message: "Nhập b_ph!" },
                              {
                                validator: (_, value) =>
                                  isNaN(parseFloat(value))
                                    ? Promise.reject("Phải là số thực!")
                                    : Promise.resolve(),
                              },
                            ]}
                            initialValue={1}
                          >
                            <Input placeholder="b_ph" />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={8} lg={4}>
                          <Form.Item
                            {...restField}
                            name={[name, "coefficients", "c_ph"]}
                            label="c_ph"
                            rules={[
                              { required: true, message: "Nhập c_ph!" },
                              {
                                validator: (_, value) =>
                                  isNaN(parseFloat(value))
                                    ? Promise.reject("Phải là số thực!")
                                    : Promise.resolve(),
                              },
                            ]}
                            initialValue={0}
                          >
                            <Input placeholder="c_ph" />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={8} lg={4}>
                          <Form.Item
                            {...restField}
                            name={[name, "coefficients", "a_sm"]}
                            label="a_sm"
                            rules={[
                              { required: true, message: "Nhập a_sm!" },
                              {
                                validator: (_, value) =>
                                  isNaN(parseFloat(value))
                                    ? Promise.reject("Phải là số thực!")
                                    : Promise.resolve(),
                              },
                            ]}
                            initialValue={0}
                          >
                            <Input placeholder="a_sm" />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={8} lg={4}>
                          <Form.Item
                            {...restField}
                            name={[name, "coefficients", "b_sm"]}
                            label="b_sm"
                            rules={[
                              { required: true, message: "Nhập b_sm!" },
                              {
                                validator: (_, value) =>
                                  isNaN(parseFloat(value))
                                    ? Promise.reject("Phải là số thực!")
                                    : Promise.resolve(),
                              },
                            ]}
                            initialValue={1}
                          >
                            <Input placeholder="b_sm" />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={8} lg={4}>
                          <Form.Item
                            {...restField}
                            name={[name, "coefficients", "c_sm"]}
                            label="c_sm"
                            rules={[
                              { required: true, message: "Nhập c_sm!" },
                              {
                                validator: (_, value) =>
                                  isNaN(parseFloat(value))
                                    ? Promise.reject("Phải là số thực!")
                                    : Promise.resolve(),
                              },
                            ]}
                            initialValue={0}
                          >
                            <Input placeholder="c_sm" />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </div>
              ))}
              <Form.Item>
                <Button
                  type="dashed"
                  disabled={isSubmitting}
                  onClick={() =>
                    add({
                      qr_string: "",
                      device_type: "",
                      status: 2,
                      lot: "3",
                      user: user?.email || "",
                      imported_at: dayjs(today),
                      tested_at: dayjs(today),
                      group: "3",
                      note: "",
                      coefficients: {
                        a_ec: 1,
                        b_ec: 0,
                        a_ph: 1,
                        b_ph: 0,
                        a_sm: 1,
                        b_sm: 0,
                      },
                    })
                  }
                  icon={<PlusOutlined />}
                  style={{ width: "100%" }}
                >
                  Thêm trường
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={isSubmitting}
            style={{ width: "100%" }}
          >
            Thêm thiết bị
          </Button>
        </Form.Item>
      </Form>

      {/* QR Scanner Modal */}
      <Modal
        title="Quét mã QR"
        open={scannerOpen}
        onCancel={() => setScannerOpen(false)}
        footer={null}
        width="100%"
        style={{ maxWidth: "400px", margin: "0 auto" }}
      >
        <QrReader
          delay={300}
          onResult={handleQrScan}
          constraints={{ facingMode: "environment" }}
          style={{ width: "100%" }}
        />
      </Modal>
    </Modal>
  );
};

export default AddDeviceForm;
